import React, { createContext, useContext, useMemo, useReducer } from 'react';
import type { Person } from '@/store/people/types';
import { useSpecificPeopleAccess } from '@/store/access/access';
import type { PeopleAccess, PeopleRoleKey } from '@/shared/types/policy';
export type DrawerTab = 'profile' | 'comments' | 'milestones';

// Define the state type
export interface DrawerState {
  open: boolean;
  currentPerson: Person | null;
  currentTab: DrawerTab;
  currentComment: string | null;
  currentMention: string | null;
}

// Define the mutations type
export type DrawerMutation =
  | { type: 'SET_OPEN'; payload: boolean }
  | { type: 'SET_CURRENT_PERSON'; payload: Person | null }
  | { type: 'SET_CURRENT_TAB'; payload: DrawerTab }
  | { type: 'SET_CURRENT_COMMENT'; payload: string | null }
  | { type: 'SET_CURRENT_MENTION'; payload: string | null };

// Define the store interface
export interface DrawerStore {
  state: DrawerState;
  access: { access: PeopleAccess; roles: PeopleRoleKey[] };
  dispatch: React.Dispatch<DrawerMutation>;
}

// Create the context
const DrawerContext = createContext<DrawerStore>({} as DrawerStore);

// Initial state
const initialState: DrawerState = {
  open: false,
  currentPerson: null,
  currentTab: 'profile',
  currentComment: null,
  currentMention: null,
};

// Reducer function
function reducer(state: DrawerState, mutation: DrawerMutation): DrawerState {
  switch (mutation.type) {
    case 'SET_OPEN':
      return { ...state, open: mutation.payload };
    case 'SET_CURRENT_PERSON':
      return { ...state, currentPerson: mutation.payload };
    case 'SET_CURRENT_TAB':
      return { ...state, currentTab: mutation.payload };
    case 'SET_CURRENT_COMMENT':
      return { ...state, currentComment: mutation.payload };
    case 'SET_CURRENT_MENTION':
      return { ...state, currentMention: mutation.payload };
    default:
      return state;
  }
}

// Provider component
export function DrawerProvider({ children }: { children: React.ReactElement }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const access = useSpecificPeopleAccess(state.currentPerson);

  const props = useMemo(
    () => ({
      state,
      access,
      dispatch,
    }),
    [state, access, dispatch],
  );

  return React.createElement(
    DrawerContext.Provider,
    { value: props },
    children,
  );
}

// Hook to use the drawer store
export function useDrawer() {
  return useContext(DrawerContext);
}

// Helper hooks for common operations
export function useDrawerActions() {
  const { dispatch } = useDrawer();

  return {
    setOpen: (open: boolean) => dispatch({ type: 'SET_OPEN', payload: open }),
    setCurrentPerson: (person: Person | null) =>
      dispatch({ type: 'SET_CURRENT_PERSON', payload: person }),
    setCurrentTab: (tab: DrawerTab) =>
      dispatch({ type: 'SET_CURRENT_TAB', payload: tab }),
    setCurrentComment: (comment: string | null) =>
      dispatch({ type: 'SET_CURRENT_COMMENT', payload: comment }),
    setCurrentMention: (mention: string | null) =>
      dispatch({ type: 'SET_CURRENT_MENTION', payload: mention }),
  };
}
